import { useState } from 'react';
import {
  Ban,
  Bot,
  Calendar,
  CalendarClock,
  Check,
  ChevronDown,
  Globe,
  HelpCircle,
  History,
  Monitor,
  User,
  XCircle,
} from 'lucide-react';
import { Appointment, AppointmentRequest } from '../services/patientApi';
import { APPOINTMENT_STATUS } from '../constants';
import { isRequestPending } from '../utils/appointmentRequests';
import {
  format24HourTo12Hour,
  formatDate,
  formatDateToISO,
  formatDateTime,
  titlise,
} from '../../utils/helpers';

// ─── Types ──────────────────────────────────────────────────────────
type ActivityState = 'done' | 'current' | 'cancelled' | 'pending';

interface ActivityIcon {
  Icon: typeof CalendarClock;
  ring: string;       // bg-* for the icon circle
  iconClass: string;  // text-* for the icon glyph
}

interface ActivityEvent {
  id: string;
  label: string;
  timestamp: string;
  state: ActivityState;
  icon: ActivityIcon;
  statusLabel?: string;
  statusClasses?: string;
  proposedDate?: string;
  proposedTime?: string;
  source?: string; // raw enum key from backend (e.g. "patient_portal")
}

// ─── Action-type meta (icon + label per request kind) ───────────────
const REQ_ICON_FALLBACK: ActivityIcon = {
  Icon: Calendar,
  ring: 'bg-gray-100',
  iconClass: 'text-secondary-600',
};

const REQ_ICON: Record<string, ActivityIcon> = {
  reschedule: { Icon: CalendarClock, ring: 'bg-blue-100',  iconClass: 'text-blue-700' },
  cancel:     { Icon: Ban,           ring: 'bg-red-100',   iconClass: 'text-red-700' },
  confirm:    { Icon: Check,         ring: 'bg-green-100', iconClass: 'text-green-700' },
};

const REQ_LABEL: Record<string, string> = {
  reschedule: 'Reschedule requested',
  cancel:     'Cancellation requested',
  confirm:    'Confirmation requested',
};

// Two pills only — anything that isn't a terminal/completed state collapses
// to "Pending". `isRequestPending` (in appointmentRequests.ts) owns the
// definition of "terminal".
const PENDING_PILL =   { label: 'Pending',   classes: 'bg-amber-100 text-amber-700' };
const COMPLETED_PILL = { label: 'Completed', classes: 'bg-gray-100 text-gray-600' };

// ─── Source meta (icon + tooltip per origin) ────────────────────────
// Backend enum: { scheduler: 0, ai_agent: 1, patient_portal: 2, web: 3 }
interface SourceMeta {
  Icon: typeof Bot;
  label: string;
}

const SOURCE_META: Record<string, SourceMeta> = {
  scheduler:      { Icon: Monitor, label: 'Office Scheduler' },
  ai_agent:       { Icon: Bot,     label: 'AI Agent' },
  patient_portal: { Icon: User,    label: 'Patient Portal' },
  web:            { Icon: Globe,   label: 'Web' },
};

const humanizeSource = (s: string) =>
  s.split(/[_\s]+/).map(titlise).join(' ');

const resolveSourceMeta = (source?: string): SourceMeta | null => {
  if (!source) return null;
  return (
    SOURCE_META[source] ?? {
      Icon: HelpCircle,
      label: humanizeSource(source) || 'Other',
    }
  );
};

// Statuses where the Booked event has actually committed to a slot. Anything
// else (unscheduled, unknown future statuses) renders the booked dot in a
// muted "not finalized" style.
const FINALIZED_BOOKING_STATUSES = new Set<string>([
  APPOINTMENT_STATUS.SCHEDULED,
  APPOINTMENT_STATUS.COMPLETED,
  APPOINTMENT_STATUS.CANCELLED,
]);

// ─── Event builder ──────────────────────────────────────────────────
const buildActivityEvents = (appt: Appointment): ActivityEvent[] => {
  const events: ActivityEvent[] = [
    {
      id: 'booked',
      label: 'Booked',
      timestamp: formatDateTime(appt.bookedAt),
      state: FINALIZED_BOOKING_STATUSES.has(appt.status) ? 'done' : 'pending',
      icon: { Icon: Calendar, ring: 'bg-gray-100', iconClass: 'text-secondary-600' },
    },
    ...appt.requests.map(toRequestEvent),
  ];

  if (appt.status === APPOINTMENT_STATUS.CANCELLED) {
    events.push({
      id: 'cancelled',
      label: 'Appointment cancelled',
      timestamp: '',
      state: 'cancelled',
      icon: { Icon: XCircle, ring: 'bg-red-100', iconClass: 'text-red-700' },
    });
  } else if (appt.status === APPOINTMENT_STATUS.COMPLETED) {
    events.push({
      id: 'completed',
      label: 'Visit completed',
      timestamp: '',
      state: 'done',
      icon: { Icon: Check, ring: 'bg-blue-100', iconClass: 'text-blue-700' },
    });
  }

  return events;
};

const toRequestEvent = (req: AppointmentRequest): ActivityEvent => {
  const icon = REQ_ICON[req.actionType] ?? REQ_ICON_FALLBACK;
  const label = REQ_LABEL[req.actionType] ?? `${titlise(req.actionType)} requested`;
  // Anything that isn't a terminal state collapses to the "Pending" pill —
  // keeps the visible vocabulary stable even when the backend introduces
  // new in-flight states.
  const isDone = !isRequestPending(req);
  const pill = isDone ? COMPLETED_PILL : PENDING_PILL;
  const proposed = req.proposedAt ? new Date(req.proposedAt) : null;
  const showProposedSlot =
    proposed !== null && !Number.isNaN(proposed.getTime()) && req.actionType === 'reschedule';
  return {
    id: req.id,
    label,
    timestamp: `Initiated at: ${formatDateTime(req.createdAt)}`,
    state: isDone ? 'done' : 'current',
    icon,
    statusLabel: pill.label,
    statusClasses: pill.classes,
    proposedDate: showProposedSlot ? formatDate(formatDateToISO(proposed!), 'long') : undefined,
    proposedTime: showProposedSlot
      ? format24HourTo12Hour(
          `${String(proposed!.getHours()).padStart(2, '0')}:${String(proposed!.getMinutes()).padStart(2, '0')}`,
        )
      : undefined,
    source: req.source,
  };
};

// ─── Sub-components ─────────────────────────────────────────────────
const SourceIconBadge = ({ meta }: { meta: SourceMeta }) => {
  const { Icon } = meta;
  return (
    <span className="relative group inline-flex">
      <span
        role="img"
        aria-label={`From ${meta.label}`}
        className="w-5 h-5 rounded-full bg-gray-100 text-secondary-500 inline-flex items-center justify-center cursor-help hover:bg-gray-200 transition-colors"
      >
        <Icon size={11} />
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-20 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity whitespace-nowrap"
      >
        <span className="block bg-secondary-900 text-white text-[10px] font-medium leading-none rounded px-2 py-1 shadow-md">
          From {meta.label}
        </span>
      </span>
    </span>
  );
};

const TimelineEntry = ({ evt, isLast }: { evt: ActivityEvent; isLast: boolean }) => {
  const { Icon } = evt.icon;
  const isCurrent = evt.state === 'current';
  const sourceMeta = resolveSourceMeta(evt.source);

  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`relative w-8 h-8 rounded-full ${evt.icon.ring} flex items-center justify-center shrink-0`}
        >
          <Icon size={14} className={evt.icon.iconClass} strokeWidth={2.25} />
          {isCurrent && (
            <span className="absolute inset-0 rounded-full ring-2 ring-amber-300 animate-pulse motion-reduce:animate-none" />
          )}
        </div>
        {!isLast && <div className="w-px flex-1 my-1 bg-gray-200" />}
      </div>
      <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-4'}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className={`text-sm font-semibold ${
              evt.state === 'pending' ? 'text-secondary-400' : 'text-secondary-900'
            }`}
          >
            {evt.label}
          </p>
          {evt.statusLabel && (
            <span
              className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${evt.statusClasses}`}
            >
              {evt.statusLabel}
            </span>
          )}
        </div>
        {(evt.timestamp || sourceMeta) && (
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {evt.timestamp && (
              <p className="text-[11px] text-secondary-400">{evt.timestamp}</p>
            )}
            {sourceMeta && <SourceIconBadge meta={sourceMeta} />}
          </div>
        )}
        {evt.proposedDate && (
          <div className="mt-2 inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1.5">
            <CalendarClock size={13} className="text-secondary-500 shrink-0" />
            <span className="text-[11px] text-secondary-700 leading-tight">
              <span className="font-semibold">{evt.proposedDate}</span>
              <span className="text-secondary-500"> · {evt.proposedTime}</span>
            </span>
          </div>
        )}
      </div>
    </li>
  );
};

const ShowEarlierToggle = ({
  hiddenCount,
  onExpand,
}: {
  hiddenCount: number;
  onExpand: () => void;
}) => {
  const label = `Show ${hiddenCount} earlier ${hiddenCount === 1 ? 'event' : 'events'}`;
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <button
          type="button"
          onClick={onExpand}
          aria-label={label}
          className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-secondary-400 hover:border-teamcare-blue hover:text-teamcare-blue transition-colors"
        >
          <ChevronDown size={14} />
        </button>
        <div className="w-px flex-1 my-1 bg-gray-200" />
      </div>
      <div className="pb-4 pt-1.5">
        <button
          type="button"
          onClick={onExpand}
          className="text-xs font-semibold text-secondary-500 hover:text-teamcare-blue transition-colors"
        >
          {label}
        </button>
      </div>
    </li>
  );
};

// ─── Public component ───────────────────────────────────────────────
const DEFAULT_VISIBLE_EVENTS = 3;

interface ActivityTimelineProps {
  appointment: Appointment;
}

/**
 * Unified chronological feed for an appointment. Combines lifecycle
 * milestones (booked, cancelled, completed) with patient-initiated request
 * events. Renders its own card chrome — drop-in on any page.
 */
export const ActivityTimeline = ({ appointment }: ActivityTimelineProps) => {
  const events = buildActivityEvents(appointment);
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max(0, events.length - DEFAULT_VISIBLE_EVENTS);
  const showToggle = hiddenCount > 0 && !expanded;
  const visible = expanded ? events : events.slice(-DEFAULT_VISIBLE_EVENTS);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <History size={14} className="text-secondary-500" />
        <h3 className="text-sm font-bold text-secondary-900">Activity</h3>
      </div>
      <ol>
        {showToggle && (
          <ShowEarlierToggle
            hiddenCount={hiddenCount}
            onExpand={() => setExpanded(true)}
          />
        )}
        {visible.map((evt, i) => (
          <TimelineEntry key={evt.id} evt={evt} isLast={i === visible.length - 1} />
        ))}
      </ol>
    </div>
  );
};
